import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Heart, Brain, Dumbbell, Users, Home, AlertTriangle, ChevronRight, X, Shield, Check, Wallet, ScanFace, Loader2, Bell } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { isWebAuthnSupported, registerBiometrics, BIOMETRIC_EMAIL_KEY } from '@/lib/webauthn';
import { subscribeToPush } from '@/utils/pushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUTO_LOGOUT_OPTIONS = [
  { value: 15, label: '15 minuter', description: 'Högsta säkerhet' },
  { value: 30, label: '30 minuter', description: 'Rekommenderat — bra balans', recommended: true },
  { value: 60, label: '1 timme', description: 'Bekvämt för aktiv användning' },
  { value: 1440, label: '24 timmar', description: 'Loggas ut efter en dags inaktivitet' },
  { value: 0, label: 'Av', description: 'Förbli alltid inloggad' },
];

const FOCUS_OPTIONS = [
  { id: 'mental', label: 'Tankar & Känslor', icon: Heart, description: 'Dagbok, reflektioner & självinsikt' },
  { id: 'health', label: 'Hälsa & Träning', icon: Dumbbell, description: 'Fitness, kost & sömn' },
  { id: 'calm', label: 'Lugn & Mindfulness', icon: Brain, description: 'Meditation, andning & fokus' },
  { id: 'relations', label: 'Relationer', icon: Users, description: 'Kommunikation & gränser' },
  { id: 'home', label: 'Hem & Vardag', icon: Home, description: 'Rutiner, städ & planering' },
  { id: 'economy', label: 'Ekonomi & Karriär', icon: Wallet, description: 'Budget, sparande & jobb' },
];

const EXPERIENCE_LEVELS = [
  { id: 'beginner', label: 'Nybörjare', description: 'Jag vill börja ta hand om mig själv' },
  { id: 'intermediate', label: 'Halvvägs', description: 'Jag har viss erfarenhet men vill mer' },
  { id: 'advanced', label: 'Erfaren', description: 'Jag vill ha avancerade verktyg' },
];

const steps = [
  {
    icon: Heart,
    title: "Välkommen till Livon",
    description: "En plats för dig som vill ha koll på ditt liv, din vardag och din utveckling. Strukturerade funktioner för att må bra och ta hand om dig själv.",
    color: "bg-blue-100"
  },
  {
    icon: Heart,
    title: "Ditt inre",
    description: "Dagbok, reflektioner, känslor och tacksamhet. Verktyg för självkännedom och emotionell balans.",
    color: "bg-blue-100"
  },
  {
    icon: Dumbbell,
    title: "Din kropp",
    description: "Träning, kost, sömn och andningsövningar. Funktioner för fysisk hälsa och energi.",
    color: "bg-emerald-100"
  },
  {
    icon: Brain,
    title: "Ditt sinne",
    description: "Minnesträning, fokus, meditation och inspiration. Verktyg för mental styrka och återhämtning.",
    color: "bg-violet-100"
  },
  {
    icon: Users,
    title: "Dina relationer",
    description: "Relationsmönster, gränser och självreflektion. Funktioner för hälsosamma relationer.",
    color: "bg-pink-100"
  },
  {
    icon: Home,
    title: "Din vardag",
    description: "Mål, påminnelser, ekonomi, karriär och rutiner. Verktyg för en strukturerad vardag.",
    color: "bg-sky-100"
  },
  {
    icon: Shield,
    title: "Skydda ditt konto",
    description: "Välj hur snabbt du loggas ut automatiskt om appen lämnas orörd — viktigt om någon annan kommer åt din enhet. Du kan ändra detta när som helst i Inställningar.",
    color: "bg-blue-100",
    id: 'security'
  },
  {
    icon: Bell,
    title: "Glöm inte bort dig själv",
    description: "Få en vänlig daglig notis som påminner dig att ta en stund för dig själv — även när appen är stängd. Du väljer själv tid och kan ändra eller stänga av under Påminnelser.",
    color: "bg-sky-100",
    id: 'notifications'
  },
  {
    icon: AlertTriangle,
    title: "Viktig information",
    description: "",
    color: "bg-amber-100",
    isDisclaimer: true
  }
];

export default function Onboarding({ onComplete }) {
  const { token, user, autoLogoutMinutes, setAutoLogoutMinutes } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedFocus, setSelectedFocus] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState('');
  const [biometricEnabled, setBiometricEnabled] = useState(!!localStorage.getItem(BIOMETRIC_EMAIL_KEY));
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);

  const notifSupported = 'Notification' in window && 'PushManager' in window && 'serviceWorker' in navigator;

  const handleEnableNotifications = async () => {
    setNotifLoading(true);
    try {
      const result = await subscribeToPush(token);
      if (!result.success) {
        if (result.reason === 'denied') {
          toast.error('Du behöver tillåta notiser i webbläsarens inställningar');
        } else {
          toast.error('Kunde inte aktivera notiser');
        }
        return;
      }
      await axios.put(`${API}/reminders/settings`, {
        reminders: { daily: { enabled: true, time: '19:00' } }
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNotifEnabled(true);
      // Don't show the dashboard popup later — already handled here
      localStorage.setItem('livon_notif_prompt_dismissed', '1');
      toast.success('Daglig påminnelse aktiverad kl 19:00');
    } catch (e) {
      toast.error('Något gick fel. Prova igen under Påminnelser.');
    } finally {
      setNotifLoading(false);
    }
  };

  const handleEnableBiometrics = async () => {
    setBiometricLoading(true);
    try {
      await registerBiometrics(token);
      localStorage.setItem(BIOMETRIC_EMAIL_KEY, user?.email);
      setBiometricEnabled(true);
      toast.success('Face ID / Touch ID aktiverat på denna enhet');
    } catch (error) {
      if (error?.name === 'NotAllowedError') {
        toast.error('Registreringen avbröts');
      } else {
        toast.error(error?.message || 'Kunde inte aktivera Face ID / Touch ID');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Save onboarding data
      const token = localStorage.getItem('token');
      if (token && selectedFocus.length > 0) {
        try {
          await axios.post(`${API}/onboarding/save`, {
            focus_areas: selectedFocus,
            experience_level: experienceLevel || 'beginner'
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e) {}
      }
      localStorage.setItem('onboarding-completed', 'true');
      onComplete();
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding-completed', 'true');
    onComplete();
  };

  const toggleFocus = (id) => {
    setSelectedFocus(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  const step = steps[currentStep];
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 bg-white dark:bg-background z-50 flex justify-center p-6 overflow-y-auto">
      <div className="max-w-md w-full my-auto py-4">
        {/* Skip button */}
        <div className="flex justify-end mb-8">
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-muted-foreground hover:text-foreground"
          >
            Hoppa över
            <X className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-8">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentStep ? 'bg-primary' : 'bg-border'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="text-center space-y-6 animate-fade-in" key={currentStep}>
          {step.id !== 'quiz' && step.id !== 'experience' && step.id !== 'security' && (
            <div className={`w-20 h-20 mx-auto rounded-2xl ${step.color} flex items-center justify-center`}>
              <Icon className={`w-9 h-9 ${step.isDisclaimer ? 'text-amber-600' : 'text-primary'}`} />
            </div>
          )}

          <h2 className="font-heading text-2xl md:text-3xl font-semibold">
            {step.title}
          </h2>

          {step.id === 'quiz' ? (
            <div className="space-y-3 text-left">
              <p className="text-sm text-muted-foreground text-center mb-4">{step.description}</p>
              {FOCUS_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => toggleFocus(opt.id)}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    selectedFocus.includes(opt.id) 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border/50 hover:border-primary/30'
                  }`}
                  data-testid={`focus-${opt.id}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selectedFocus.includes(opt.id) ? 'bg-primary/10' : 'bg-muted'}`}>
                    <opt.icon className={`w-5 h-5 ${selectedFocus.includes(opt.id) ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm">{opt.label}</p>
                    <p className="text-xs text-muted-foreground">{opt.description}</p>
                  </div>
                  {selectedFocus.includes(opt.id) && <Check className="w-5 h-5 text-primary" />}
                </button>
              ))}
            </div>
          ) : step.id === 'experience' ? (
            <div className="space-y-3 text-left">
              <p className="text-sm text-muted-foreground text-center mb-4">{step.description}</p>
              {EXPERIENCE_LEVELS.map(lvl => (
                <button
                  key={lvl.id}
                  onClick={() => setExperienceLevel(lvl.id)}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    experienceLevel === lvl.id 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border/50 hover:border-primary/30'
                  }`}
                  data-testid={`experience-${lvl.id}`}
                >
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm">{lvl.label}</p>
                    <p className="text-xs text-muted-foreground">{lvl.description}</p>
                  </div>
                  {experienceLevel === lvl.id && <Check className="w-5 h-5 text-primary" />}
                </button>
              ))}
            </div>
          ) : step.id === 'security' ? (
            <div className="space-y-3 text-left">
              <p className="text-sm text-muted-foreground text-center mb-4">{step.description}</p>
              <p className="text-sm font-medium">Automatisk utloggning vid inaktivitet:</p>
              {AUTO_LOGOUT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setAutoLogoutMinutes(opt.value)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    autoLogoutMinutes === opt.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border/50 hover:border-primary/30'
                  }`}
                  data-testid={`auto-logout-option-${opt.value}`}
                >
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm">
                      {opt.label}
                      {opt.recommended && <span className="ml-2 text-xs text-primary font-normal">Rekommenderas</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{opt.description}</p>
                  </div>
                  {autoLogoutMinutes === opt.value && <Check className="w-5 h-5 text-primary" />}
                </button>
              ))}

              {isWebAuthnSupported() && (
                <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-border/50 mt-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <ScanFace className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Face ID / Touch ID</p>
                    <p className="text-xs text-muted-foreground">Logga in snabbt med biometri (valfritt)</p>
                  </div>
                  {biometricEnabled ? (
                    <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                      <Check className="w-4 h-4" /> Aktiverad
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      onClick={handleEnableBiometrics}
                      disabled={biometricLoading}
                      className="rounded-full"
                      data-testid="onboarding-enable-biometric-btn"
                    >
                      {biometricLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aktivera'}
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : step.id === 'notifications' ? (
            <div className="space-y-4 text-left">
              <p className="text-sm text-muted-foreground text-center">{step.description}</p>
              {notifSupported ? (
                <div className="flex items-center gap-3 p-4 rounded-xl border-2 border-border/50">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Bell className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Daglig påminnelse kl 19:00</p>
                    <p className="text-xs text-muted-foreground">"Hur har din dag varit? Ta en stund för dig själv."</p>
                  </div>
                  {notifEnabled ? (
                    <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                      <Check className="w-4 h-4" /> Aktiverad
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      onClick={handleEnableNotifications}
                      disabled={notifLoading}
                      className="rounded-full"
                      data-testid="onboarding-enable-notifications-btn"
                    >
                      {notifLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aktivera'}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-sm text-sky-800">
                  Notiser kräver att Livon är installerad på hemskärmen (på iPhone: iOS 16.4 eller senare).
                  Installera appen först, sedan kan du aktivera påminnelser under Planering → Påminnelser.
                </div>
              )}
              <p className="text-xs text-muted-foreground text-center">
                Helt valfritt — tryck Nästa för att hoppa över.
              </p>
            </div>
          ) : step.isDisclaimer ? (
            <div className="text-left space-y-4">
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                <p className="text-sm text-amber-800 dark:text-amber-300 font-semibold mb-2">
                  Livon erbjuder inte medicinsk vård, psykoterapi eller diagnos.
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Vid psykisk ohälsa rekommenderas kontakt med legitimerad vårdgivare.
                </p>
              </div>
              
              <div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-900 rounded-xl p-4">
                <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold">GDPR & Dataskydd</p>
                  <p className="text-sm text-muted-foreground">
                    Din data lagras säkert och delas aldrig med tredje part.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          )}
        </div>

        {/* Navigation */}
        <div className="mt-12">
          <Button
            onClick={handleNext}
            className="w-full rounded-full py-6 text-base btn-warm"
            disabled={step.id === 'quiz' && selectedFocus.length === 0}
          >
            {currentStep === steps.length - 1 ? 'Jag förstår — Börja nu' : 'Nästa'}
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* Step indicator */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          {currentStep + 1} av {steps.length}
        </p>
      </div>
    </div>
  );
}
